import { useFormikContext } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import type { UserOrganisation } from '../../../../../../app/ApiGen';
import { useCurrentOrganisation } from '../../../../../../app/CurrentOrganisationProvider';
import { ConnectorsInput } from '../../../../../../components/CustomFields/Connectors';
import type { ConnectorFormFields } from '../../../../../../components/CustomFields/Connectors/ConnectorSection';
import {
  OCPP_CHARGE_POINT_ID_MAX_LENGTH,
  PREFIX_NAME_MAX_LENGTH,
  PrefixText,
  type WizardOcppChargePointId,
} from '../../../../../../components/CustomFields/PrefixText';
import { FormEditable } from '../../../../../../components/Form/components/FormEditable';
import { type InputComponentProps, Section } from '../../../../../../design-system';
import { createSchema, type FormFields } from '../../../../../../utils/formUtils';
import type { DetailsOther } from '../types';
import { DetailsStepForm } from './DetailsStepForm';

const detailFields: FormFields<Pick<DetailsOther, 'ocppChargePointId'>, string> = {
  ocppChargePointId: {
    id: 'ocppChargePointId',
    label: 'OCPP Charge Point ID',
    inputComponent: ({
      name,
      disabled,
      error,
      helperText,
      value,
      onBlur,
      onChange,
    }: InputComponentProps<WizardOcppChargePointId, string>) => (
      <PrefixText
        name={name}
        disabled={disabled}
        error={error}
        helperText={helperText}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
      />
    ),
    inputProps: { helperText: 'OCPP Charge Point ID' },
  },
};

const connectorFields: FormFields<Pick<DetailsOther, 'connectors'>> = {
  connectors: { id: 'connectors', label: 'Connectors', inputComponent: ConnectorsInput },
};

/* eslint no-template-curly-in-string: "off" --- Yup messages can be customised using template curlies in strings. */
const validationSchema = createSchema<DetailsOther>({
  ocppChargePointId: Yup.object()
    .shape<WizardOcppChargePointId>({
      prefix: Yup.string().trim().required('OCPP Charge Point ID prefix is required'),
      suffix: Yup.string()
        .min(3, 'OCPP Charge Point ID must be at least ${min} characters')
        .max(
          OCPP_CHARGE_POINT_ID_MAX_LENGTH - PREFIX_NAME_MAX_LENGTH,
          `OCPP Charge ID must be at most ${OCPP_CHARGE_POINT_ID_MAX_LENGTH} characters`,
        )
        .matches(
          /^[A-Z0-9-]*$/,
          'OCPP Charge Point Id may only include upper case letters, numbers, and "-" characters',
        )
        .required('OCPP Charge Point ID is required'),
    })
    .required(),
  connectors: Yup.array<ConnectorFormFields>()
    .min(1, 'At least one connector is required')
    .required('Connector is required'),
});

const getPrefix = (organisation?: UserOrganisation): string => {
  if (organisation) {
    const { namespacePrefix } = organisation;
    if (namespacePrefix !== '') {
      return namespacePrefix;
    }
  }
  return 'EVNEX';
};

const ConnectorsFormField: React.FC = () => {
  const { errors, isSubmitting, touched } = useFormikContext<Pick<DetailsOther, 'connectors'>>();

  return (
    <Section pt={3} title="Connectors" description="Settings and configuration for your charge point.">
      <FormEditable fields={connectorFields} errors={errors} touched={touched} disabled={isSubmitting} />
    </Section>
  );
};

export const DetailsOtherStep: React.FC = () => {
  const { organisation } = useCurrentOrganisation();

  const initialValues: DetailsOther = {
    ocppChargePointId: { prefix: getPrefix(organisation), suffix: '' },
    connectors: [],
  };

  return (
    <DetailsStepForm fields={detailFields} initialValues={initialValues} validationSchema={validationSchema}>
      <ConnectorsFormField />
    </DetailsStepForm>
  );
};
